import { createColumnHelper } from '@tanstack/react-table';
import { LocalizedCurrency, LocalizedPercentage, lt } from '@mews/framework';
import { Body, ColumnFilterType, Tag } from '@optimus-web/core';
import { Target, UnicodeEntity } from '@mews/enums';
import {
    PortfolioProductOverviewEntry,
    PortfolioProductPricing,
    PortfolioRelativeProductPrice,
} from '@mews-commander/data-access';
import { Dash, EnterpriseLink, TooltippedSpan } from '@mews-commander/ui';
import { EnrichedTaxValues } from '../../../utils/mapEntryWithEnrichedTaxes';
import { LocalProductTaxCell } from './LocalProductTaxCell';
import { RowActionCell } from './RowActionCell';

const columnHelper = createColumnHelper<PortfolioProductOverviewEntry>();

interface Props {
    isIncludeTax: boolean;
}

export const useLocalProductTableColumns = ({ isIncludeTax }: Props) => {
    const columns = [
        columnHelper.accessor('name', {
            header: () => lt('InternalName'),
            cell: ({ row, getValue }) => {
                const {
                    id: productId,
                    property: { id: currentEnterpriseId },
                } = row.original;
                return (
                    <EnterpriseLink
                        target={Target.Blank}
                        standalone
                        variant="basic"
                        to="Product/Detail/$productId"
                        params={{
                            currentEnterpriseId,
                            productId,
                        }}
                    >
                        {getValue()}
                    </EnterpriseLink>
                );
            },
        }),
        columnHelper.accessor('externalIdentifier', {
            header: () => lt('ExternalIdentifier'),
            enableSorting: true,
            enableColumnFilter: true,
            meta: {
                filtersType: ColumnFilterType.String,
            },
            cell: (props) => {
                const externalIdentifier = props.getValue();
                return externalIdentifier || <Dash />;
            },
        }),
        columnHelper.accessor('property', {
            header: () => lt('Property'),
            enableSorting: true,
            enableColumnFilter: true,
            meta: {
                filtersType: ColumnFilterType.String,
            },
            cell: (props) => {
                const property = props.getValue();
                return (
                    <Tag key={property.id} variant="basicSubtle">
                        <Body ellipsis>{property.name}</Body>
                    </Tag>
                );
            },
        }),
        columnHelper.accessor('service', {
            header: () => lt('Service'),
            cell: (props) => {
                const service = props.getValue();
                return (
                    <Tag key={service.id} variant="basicSubtle">
                        <Body ellipsis>{service.name}</Body>
                    </Tag>
                );
            },
        }),
        columnHelper.accessor('accountingCategory', {
            header: () => lt('Accounting'),
            enableColumnFilter: true,
            meta: {
                filtersType: ColumnFilterType.String,
            },
            cell: (props) => {
                const accountingCategory = props.getValue();
                return accountingCategory?.name || <Dash />;
            },
        }),
        columnHelper.accessor('ordering', {
            header: () => lt('Ordering'),
            enableSorting: true,
        }),
        columnHelper.accessor('pricing', {
            header: () => lt('TaxRate'),
            cell: (props) => {
                const taxRate = props.getValue();

                // taxValues have their own discriminator, therefore we do not use the
                // taxRate discriminator as this does only apply to basePrice
                return taxRate.discriminator === PortfolioProductPricing.Absolute ? (
                    <LocalProductTaxCell data={taxRate?.value.taxValues as EnrichedTaxValues[]} />
                ) : (
                    <LocalProductTaxCell
                        data={
                            (taxRate?.value as PortfolioRelativeProductPrice & { taxValues: EnrichedTaxValues[] })
                                .taxValues
                        }
                    />
                );
            },
        }),
        columnHelper.accessor('pricing', {
            header: () => (isIncludeTax ? lt('BasePriceIncludeTax') : lt('BasePriceExcludeTax')),
            cell: (props) => {
                const { discriminator, value } = props.getValue();

                return discriminator === PortfolioProductPricing.Absolute ? (
                    <TooltippedSpan
                        tooltip={
                            isIncludeTax ? (
                                <>
                                    {lt('BasePriceExcludeTax')}
                                    {UnicodeEntity.Colon}
                                    {UnicodeEntity.Space}
                                    <LocalizedCurrency currency={value.currency} value={value.netValue} />
                                </>
                            ) : (
                                <>
                                    {lt('BasePriceIncludeTax')}
                                    {UnicodeEntity.Colon}
                                    {UnicodeEntity.Space}
                                    <LocalizedCurrency currency={value.currency} value={value.grossValue} />
                                </>
                            )
                        }
                    >
                        <LocalizedCurrency
                            currency={value.currency}
                            value={isIncludeTax ? value.grossValue : value.netValue}
                        />
                    </TooltippedSpan>
                ) : (
                    <LocalizedPercentage value={value.multiplier} />
                );
            },
        }),
        columnHelper.display({
            id: 'actions',
            meta: { align: 'end' },
            cell: ({ row }) => {
                const {
                    id: productId,
                    property: { id: currentEnterpriseId },
                } = row.original;
                return <RowActionCell productId={productId} currentEnterpriseId={currentEnterpriseId} />;
            },
        }),
    ];

    return { columns };
};
